// ft3d-hud/feed-tabs.ts — the All / Chat / Combat tabs over the shared feed (owner 2026-09-16).
//
// One stream, three views. Rolls and speech live in the same line list (log.ts owns the roll lines,
// chat.ts appends `.is-chat` ones), so they interleave in time; a tab only decides which kinds are
// SHOWN, which is pure CSS off `data-ft3d-feed-filter` on the feed root. Nothing is unmounted, so
// switching tabs never loses a line and never re-fetches anything.
//
// The rim's chat button (my_campaign.php) calls the exported helper to jump straight to Chat and put
// the cursor in the field — that is the whole "open chat" gesture for a mouse user.

export type FtFeedTab = 'all' | 'chat' | 'combat';

const TABS: FtFeedTab[] = ['all', 'chat', 'combat'];
const TAB_KEY = 'ft3d.hud.feedTab';

let feedEl: HTMLElement | null = null;
let current: FtFeedTab = 'all';

function readTab(): FtFeedTab {
    try {
        const stored = window.localStorage.getItem(TAB_KEY);
        if (stored && (TABS as string[]).includes(stored)) return stored as FtFeedTab;
    } catch { /* private windows throw; the default is fine */ }
    return 'all';
}

function applyTab(): void {
    if (!feedEl) return;
    feedEl.setAttribute('data-ft3d-feed-filter', current);
    feedEl.querySelectorAll<HTMLElement>('[data-ft3d-feed-tab]').forEach((tab) => {
        const active = tab.dataset.ft3dFeedTab === current;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    // A filtered view is read bottom-up like the unfiltered one.
    const stream = feedEl.querySelector<HTMLElement>('[data-ft3d-log]');
    if (stream) stream.scrollTop = stream.scrollHeight;
}

export function setFt3dFeedTab(next: FtFeedTab): void {
    if (!TABS.includes(next)) return;
    current = next;
    try { window.localStorage.setItem(TAB_KEY, next); } catch { /* viewer-local nicety only */ }
    applyTab();
}

export function getFt3dFeedTab(): FtFeedTab {
    return current;
}

/** The rim's chat button: show the Chat tab and start typing. */
export function openFt3dChatTab(): void {
    setFt3dFeedTab('chat');
    (window as { ftFt3dChatOpen?: (prefill?: string) => void }).ftFt3dChatOpen?.();
}

export function mountFt3dFeedTabs(host: HTMLElement): void {
    feedEl = host.querySelector<HTMLElement>('[data-ft3d-feed]');
    if (!feedEl) return;
    current = readTab();
    feedEl.querySelectorAll<HTMLElement>('[data-ft3d-feed-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
            const next = tab.dataset.ft3dFeedTab as FtFeedTab | undefined;
            if (next) setFt3dFeedTab(next);
        });
    });
    applyTab();

    // ⛔ No rim button (owner 2026-09-16 — scrapped): Enter opens the line, the tabs switch the view.
    // The binding stays available for any route that chooses to add its own control.
    document.querySelectorAll<HTMLElement>('[data-ft3d-chat-tab-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
            // A second press on an already-open Chat tab goes back to All, so one control does both.
            if (current === 'chat' && (window as { ftFt3dChatIsOpen?: () => boolean }).ftFt3dChatIsOpen?.()) {
                setFt3dFeedTab('all');
                return;
            }
            openFt3dChatTab();
        });
    });

    (window as { ftSetFt3dFeedTab?: typeof setFt3dFeedTab }).ftSetFt3dFeedTab = setFt3dFeedTab;
    (window as { ftOpenFt3dChatTab?: typeof openFt3dChatTab }).ftOpenFt3dChatTab = openFt3dChatTab;
}
